import { useState, useEffect } from 'react'
import { ShieldCheck, ArrowLeftRight, ChevronDown } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useStackAbove } from '@/lib/useStackAbove'

const MINIMIZED_KEY = 'runna_godmode_banner_minimized'

/**
 * Badge verde flutuante quando platform admin está "dentro" de um tenant via
 * god mode (identidade preservada, poderes de admin). Distinto do banner
 * roxo de impersonation — lá você finge ser outra pessoa, aqui você é você
 * mesmo com acesso expandido.
 */
export default function GodModeBanner() {
  const { isPlatformAdmin, impersonating, profile } = useAuth()
  const [minimized, setMinimized] = useState<boolean>(() => {
    return sessionStorage.getItem(MINIMIZED_KEY) !== '0'
  })

  // Empilha acima do EnvBadge (sempre presente em DEV) e do ImpersonationBanner
  // (em estados intermediários de transição entre os dois modos).
  const stackOffset = useStackAbove(['[data-env-badge]', '[data-impersonation-banner]'])

  useEffect(() => {
    sessionStorage.setItem(MINIMIZED_KEY, minimized ? '1' : '0')
  }, [minimized])

  if (!isPlatformAdmin || impersonating || !profile?.tenant) return null

  const host = typeof window !== 'undefined' ? window.location.hostname : ''
  const isAdminHost = host === 'admin.runnaflow.com' || host === 'admin-dev.runnaflow.com'
  if (isAdminHost) return null

  const adminOrigin =
    host === 'runnaflow.com' || host === 'www.runnaflow.com'
      ? 'https://admin.runnaflow.com'
    : host === 'runnaflow-dev.runnaflow.com'
      ? 'https://admin-dev.runnaflow.com'
    : null

  function handleTrocarTenant() {
    try { sessionStorage.removeItem('runna_god_mode_active') } catch { /* ignore */ }
    if (adminOrigin) window.location.href = `${adminOrigin}/tenants`
    else window.location.href = '/'
  }

  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="fixed right-4 z-[55] flex items-center gap-1.5 px-2.5 py-1.5 rounded-full shadow-lg cursor-pointer transition-all hover:brightness-110"
        style={{
          background: 'var(--color-accent-green)',
          color: '#063e26',
          bottom: `calc(1rem + var(--mobile-bottom-bar-pad, 0px) + ${stackOffset}px)`,
        }}
        title={`God mode · ${profile.tenant.name}`}
        aria-label="Expandir banner de god mode"
      >
        <ShieldCheck className="w-3.5 h-3.5" />
        <span className="text-[11px] font-semibold hidden sm:inline">Admin</span>
        <ChevronDown className="w-3 h-3 opacity-70 rotate-180" />
      </button>
    )
  }

  return (
    <div
      className="fixed right-4 z-[55] rounded-xl shadow-xl p-3 max-w-[calc(100vw-2rem)] w-[320px]"
      style={{
        background: 'var(--color-accent-green)',
        color: '#063e26',
        bottom: 'calc(1rem + var(--mobile-bottom-bar-pad, 0px))',
      }}
    >
      <div className="flex items-start gap-2 mb-2">
        <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0 text-[12px] leading-[1.35]">
          Platform Admin · <strong className="font-semibold">{profile.tenant.name}</strong>
          <div className="opacity-80 text-[11px] mt-0.5">
            {profile.user.name} com acesso total
          </div>
        </div>
        <button
          onClick={() => setMinimized(true)}
          className="opacity-70 hover:opacity-100 transition-opacity shrink-0 cursor-pointer"
          title="Minimizar"
          aria-label="Minimizar"
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </div>
      <button
        onClick={handleTrocarTenant}
        className="w-full flex items-center justify-center gap-1.5 text-[12px] font-semibold px-3 py-2 rounded-md cursor-pointer transition-colors"
        style={{ background: 'rgba(0,0,0,0.1)' }}
        title="Voltar ao painel admin pra escolher outro tenant"
      >
        <ArrowLeftRight className="w-3.5 h-3.5" />
        Trocar tenant
      </button>
    </div>
  )
}
